import { createContext, useContext, useState, useCallback } from 'react';
import { getTour } from '@/components/tour/tourSteps';

const TourCtx = createContext(null);

const LS_COMPLETED = 'tagdada_tours_completed';
const LS_WELCOME = 'tagdada_tour_welcome_seen';
const LS_LAUNCHER_HIDDEN = 'tagdada_tour_launcher_hidden';
const SS_WELCOME_CLOSED_AT = 'tagdada_welcome_closed_at';

function readCompleted() {
  try { return JSON.parse(localStorage.getItem(LS_COMPLETED) || '[]'); }
  catch { return []; }
}

// Anchor the PWA-install banner countdown to the moment the welcome popup is
// closed (kept in sessionStorage so it re-arms on each fresh login).
function markWelcomeClosed() {
  try {
    if (!sessionStorage.getItem(SS_WELCOME_CLOSED_AT)) {
      sessionStorage.setItem(SS_WELCOME_CLOSED_AT, String(Date.now()));
    }
  } catch { /* ignore */ }
}

export function TourProvider({ children }) {
  const [activeId, setActiveId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [completed, setCompleted] = useState(readCompleted);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [launcherHidden, setLauncherHidden] = useState(() => {
    try { return !!localStorage.getItem(LS_LAUNCHER_HIDDEN); } catch { return false; }
  });

  const hideLauncher = useCallback(() => {
    setLauncherHidden(true);
    try { localStorage.setItem(LS_LAUNCHER_HIDDEN, '1'); } catch {}
  }, []);

  const showLauncher = useCallback(() => {
    setLauncherHidden(false);
    try { localStorage.removeItem(LS_LAUNCHER_HIDDEN); } catch {}
  }, []);

  const startTour = useCallback((id) => {
    if (!getTour(id)) return;
    setWelcomeOpen(false);
    markWelcomeClosed();
    try { localStorage.setItem(LS_WELCOME, '1'); } catch {}
    setStepIndex(0);
    setActiveId(id);
  }, []);

  const stopTour = useCallback(() => {
    setActiveId(null);
    setStepIndex(0);
  }, []);

  const markCompleted = useCallback((id) => {
    setCompleted((prev) => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      try { localStorage.setItem(LS_COMPLETED, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const finishTour = useCallback(() => {
    setActiveId((id) => { if (id) markCompleted(id); return null; });
    setStepIndex(0);
  }, [markCompleted]);

  const nextStep = useCallback(() => {
    setStepIndex((i) => {
      const tour = getTour(activeId);
      if (!tour) return i;
      return Math.min(tour.steps.length - 1, i + 1);
    });
  }, [activeId]);

  const prevStep = useCallback(() => setStepIndex((i) => Math.max(0, i - 1)), []);

  const openWelcome = useCallback(() => setWelcomeOpen(true), []);
  // "Maybe later" — close for now; do NOT persist, so it returns next login.
  const dismissWelcome = useCallback(() => {
    setWelcomeOpen(false);
    markWelcomeClosed();
  }, []);
  // "Never ask again" — permanently suppress auto-prompt only; FAB / Tutorials can still reopen.
  const dismissWelcomeForever = useCallback(() => {
    setWelcomeOpen(false);
    markWelcomeClosed();
    try { localStorage.setItem(LS_WELCOME, '1'); } catch {}
  }, []);

  const value = {
    activeId, stepIndex, completed, welcomeOpen,
    startTour, stopTour, finishTour, nextStep, prevStep,
    openWelcome, dismissWelcome, dismissWelcomeForever, markWelcomeClosed,
    launcherHidden, hideLauncher, showLauncher,
    welcomeSeen: () => { try { return !!localStorage.getItem(LS_WELCOME); } catch { return true; } },
  };

  return <TourCtx.Provider value={value}>{children}</TourCtx.Provider>;
}

export function useTour() {
  const ctx = useContext(TourCtx);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
}
